import { defineStore } from 'pinia'
import { ref } from 'vue'
import type {
  BackendTeam,
  CreateTeamInput,
  PatchTeamInput,
  PullSummary,
  AddMemberInput,
  PatchMemberInput,
} from '@teamofone/api-client'
import { teamsApi, currentDevIdentity } from '@/services/teamsClient'

export const useTeamsStore = defineStore('teams', () => {
  const teams = ref<BackendTeam[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const pulling = ref<Record<string, boolean>>({})
  const lastPullSummaries = ref<PullSummary[]>([])

  async function refresh(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const id = currentDevIdentity()
      teams.value = await teamsApi.list(id)
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Failed to load teams'
    } finally {
      loading.value = false
    }
  }

  async function create(input: CreateTeamInput): Promise<BackendTeam | null> {
    error.value = null
    try {
      const id = currentDevIdentity()
      const fresh = await teamsApi.create(id, input)
      teams.value = [...teams.value.filter((t) => t.teamId !== fresh.teamId), fresh]
      return fresh
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Failed to create team'
      return null
    }
  }

  /**
   * Create a squad from the bundled sub-team catalog. The backend resolves
   * prefill server-side and derives the squad name from the catalog; we only
   * forward the catalog key. Returns the freshly-created squad or null on a
   * known error (the picker surfaces the readable message).
   */
  async function createWithSubteam(subTeamKey: string): Promise<BackendTeam | null> {
    return create({ subTeamKey })
  }

  async function patch(teamId: string, p: PatchTeamInput): Promise<BackendTeam | null> {
    error.value = null
    try {
      const id = currentDevIdentity()
      const fresh = await teamsApi.patch(id, teamId, p)
      teams.value = teams.value.map((t) => (t.teamId === teamId ? fresh : t))
      return fresh
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Failed to update team'
      return null
    }
  }

  async function remove(teamId: string): Promise<boolean> {
    error.value = null
    try {
      const id = currentDevIdentity()
      await teamsApi.delete(id, teamId)
      teams.value = teams.value.filter((t) => t.teamId !== teamId)
      return true
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Failed to delete team'
      return false
    }
  }

  async function pullNow(teamId: string): Promise<PullSummary[]> {
    error.value = null
    pulling.value = { ...pulling.value, [teamId]: true }
    try {
      const id = currentDevIdentity()
      const res = await teamsApi.pullGithub(id, teamId)
      lastPullSummaries.value = res.summaries
      return res.summaries
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Pull failed'
      return []
    } finally {
      pulling.value = { ...pulling.value, [teamId]: false }
    }
  }

  async function addMember(teamId: string, input: AddMemberInput): Promise<BackendTeam | null> {
    error.value = null
    try {
      const id = currentDevIdentity()
      const fresh = await teamsApi.addMember(id, teamId, input)
      teams.value = teams.value.map((t) => (t.teamId === teamId ? fresh : t))
      return fresh
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Failed to add member'
      return null
    }
  }

  async function patchMember(
    teamId: string,
    userId: string,
    p: PatchMemberInput,
  ): Promise<BackendTeam | null> {
    error.value = null
    try {
      const id = currentDevIdentity()
      const fresh = await teamsApi.patchMember(id, teamId, userId, p)
      teams.value = teams.value.map((t) => (t.teamId === teamId ? fresh : t))
      return fresh
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Failed to update member'
      return null
    }
  }

  async function removeMember(teamId: string, userId: string): Promise<boolean> {
    error.value = null
    try {
      const id = currentDevIdentity()
      const fresh = await teamsApi.removeMember(id, teamId, userId)
      teams.value = teams.value.map((t) => (t.teamId === teamId ? fresh : t))
      return true
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'Failed to remove member'
      return false
    }
  }

  return {
    teams,
    loading,
    error,
    pulling,
    lastPullSummaries,
    refresh,
    create,
    createWithSubteam,
    patch,
    remove,
    pullNow,
    addMember,
    patchMember,
    removeMember,
  }
})
